// ============================================================
// aluminumCalc.js
// Aluminum fence estimator engine.
//
// Mirrors src/utils/vinylCalc.js's structure and output contract so it is a
// drop-in for the same Estimator UI → NewDocument/EditDocument flow:
//   - export function calculateAluminum(specs, products, priceLevel = 'A')
//   - returns exactly { items, warnings }
//   - identical lookupSKU / getPrice / addItem semantics and line-item shape
//   - final SKU-merge pass (same-sku lines summed, sku-less lines appended last)
//
// Prices are NOT here — line prices are resolved by SKU against the Price List
// (Firestore `products`) at the given priceLevel (A/B/C).
//
// Product: residential aluminum fence — one style, 54" tall, 6'-wide panels.
//
// Differences from vinylCalc (everything else is a straight mirror):
//   - PANEL DIVISOR IS 6, not 8.
//   - FOUR post types. Vinyl folds gate posts into the user's end-post count;
//     aluminum derives them from the gates (single = 1, double = 2).
//   - NO POST CAPS. Vinyl adds 1 cap per post; aluminum has no cap part.
//   - NO STIFFENER. Vinyl adds 1 per leaf; aluminum gates need none.
//   - Gate panels vary by width AND top shape, so the lookup is two-level.
//
// Rules (implemented in this section order):
//   1) Panels      — per run: ceil(length / 6); summed across runs.
//   2) Line posts  — per run: length < 6 → 0, else ceil(length / 6) - 1; summed.
//   3) Corner posts— totalCorners (user-entered).
//   4) End posts   — totalEnds (user-entered; includes the closing-side end post
//                    of each SINGLE gate, which the user counts themselves).
//   5) Gate posts  — per gate: double ? 2 : 1 (one per leaf).
//   6) Gates       — per gate: leaves = double?2:1.
//                    1 gate panel/leaf + 1 hinge set/leaf, 1 latch per gate
//                    (single OR double pair; type set job-wide, default
//                    standard), and 1 drop rod per DOUBLE pair only.
//
// Bad input is tolerated: runs/gates default to [], numeric fields to 0,
// non-positive run lengths are ignored, and a gate with an unknown width or
// shape resolves to an undefined panel SKU that surfaces as PRODUCT NOT FOUND.
// ============================================================

import {
  ALUMINUM_PANEL_SKU, ALUMINUM_LINE_POST_SKU, ALUMINUM_CORNER_POST_SKU,
  ALUMINUM_END_POST_SKU, ALUMINUM_GATE_POST_SKU,
  ALUMINUM_GATE_PANEL_SKU, ALUMINUM_GATE_HINGE_SKU,
  ALUMINUM_LATCH_SKU, ALUMINUM_DROP_ROD_SKU,
} from '../data/aluminumSKUs';

const lookupSKU = (sku, products) => products.find(p => p.sku === sku && p.isActive);

// Panels are 6' wide (vinyl's are 8'). Used by BOTH the panel count and the
// line-post count below, which is why it is named once here.
const PANEL_WIDTH_FT = 6;

export function calculateAluminum(specs, products, priceLevel = 'A') {
  const {
    runs = [],
    totalCorners = 0,
    totalEnds = 0,
    latchType = 'standard',
    gates = [],
  } = specs || {};

  const warnings = [];
  const items = [];

  const getPrice = (product) => {
    if (!product) return 0;
    if (priceLevel === 'B') return parseFloat(product.priceB || 0);
    if (priceLevel === 'C') return parseFloat(product.priceC || 0);
    return parseFloat(product.priceA || 0);
  };

  // Mirrors vinylCalc's addItem exactly: guards only on qty, so an
  // undefined/missing SKU still surfaces as a "PRODUCT NOT FOUND" warning line —
  // e.g. a gate whose width/shape isn't a known combination.
  const addItem = (sku, qty, fallbackLabel, unit = 'pc', roundable = false) => {
    if (qty <= 0) return;
    const product = lookupSKU(sku, products);
    if (!product) warnings.push(sku);
    items.push({
      id: Date.now() + Math.random(),
      sku,
      description: product ? product.description : `⚠️ PRODUCT NOT FOUND - SKU: ${sku}`,
      qty: Math.ceil(qty),
      unit: product?.unit || unit,
      unitPrice: getPrice(product),
      isStock: product?.isStock ?? true,
      productId: product?.id || null,
      found: !!product,
      roundable,
      fromEstimator: true,
      estimatorKind: 'aluminum',
    });
  };

  const safeRuns = Array.isArray(runs) ? runs : [];
  const safeGates = Array.isArray(gates) ? gates : [];
  const corners = parseInt(totalCorners) || 0;
  const ends = parseInt(totalEnds) || 0;

  // 1) Panels — per run: ceil(length / 6), summed.
  let totalPanels = 0;
  safeRuns.forEach(r => {
    const len = parseFloat(r?.length) || 0;
    if (len <= 0) return;
    totalPanels += Math.ceil(len / PANEL_WIDTH_FT);
  });
  addItem(ALUMINUM_PANEL_SKU, totalPanels, 'Aluminum Panel 54in x 6ft');

  // 2) Line posts — per run: length < 6 → 0, else ceil(length / 6) - 1, summed.
  // The guard here is `< 6` while the panel loop above guards `<= 0`: a short
  // run still takes a (cut-down) panel but has no intermediate post. This
  // asymmetry is inherited from vinylCalc deliberately.
  let totalLinePosts = 0;
  safeRuns.forEach(r => {
    const len = parseFloat(r?.length) || 0;
    if (len < PANEL_WIDTH_FT) return; // includes non-positive lengths → 0 line posts
    totalLinePosts += Math.ceil(len / PANEL_WIDTH_FT) - 1;
  });
  addItem(ALUMINUM_LINE_POST_SKU, totalLinePosts, 'Aluminum Line Post');

  // 3) Corner posts
  addItem(ALUMINUM_CORNER_POST_SKU, corners, 'Aluminum Corner Post');

  // 4) End posts — user-entered. Unlike vinyl this does NOT include gate posts,
  // but it DOES include the closing-side end post of each single gate (a single
  // gate hangs on one gate post and latches to an end post the user counts).
  addItem(ALUMINUM_END_POST_SKU, ends, 'Aluminum End Post');

  // 5) Gate posts — aluminum-specific. One per leaf: a single gate hangs on 1
  // gate post, a double pair uses 2. Vinyl has no equivalent line.
  const gatePosts = safeGates.reduce((sum, g) => sum + (g?.kind === 'double' ? 2 : 1), 0);
  addItem(ALUMINUM_GATE_POST_SKU, gatePosts, 'Aluminum Gate Post');

  // NO POST CAPS. Vinyl adds 1 cap per post here; aluminum posts ship capped, so
  // there is deliberately no cap line. See the note in aluminumSKUs.js.

  // 6) Gates — prebuilt panel(s) + hardware per gate. addItem merges by SKU at the
  // end, so multiple gates of the same width/shape/type sum correctly.
  const latchSku = ALUMINUM_LATCH_SKU[latchType || 'standard'];
  safeGates.forEach(gate => {
    const width = gate?.width;
    const shape = gate?.shape;
    const kind = gate?.kind;
    const leaves = kind === 'double' ? 2 : 1;

    // Two-level lookup. The optional chain on the SECOND level matters: an
    // unknown width would otherwise make this throw instead of degrading to a
    // PRODUCT NOT FOUND line the way vinyl's single-level lookup does.
    const gatePanelSku = ALUMINUM_GATE_PANEL_SKU[width]?.[shape];
    addItem(gatePanelSku, leaves, 'Aluminum Gate Panel');          // 1 per leaf
    addItem(ALUMINUM_GATE_HINGE_SKU, leaves, 'Gate Hinge Set');    // 1 pair per leaf
    addItem(latchSku, 1, 'Gate Latch');                            // 1 per gate (single OR double pair)
    if (kind === 'double') addItem(ALUMINUM_DROP_ROD_SKU, 1, 'Drop Rod'); // 1 per double pair only
  });

  // Merge items with the same SKU into one line (qtys summed, first occurrence's
  // other fields kept). Sku-less lines (undefined sku or '—') are appended last.
  const skuMap = new Map();
  const noSkuItems = [];
  items.forEach(item => {
    if (!item.sku || item.sku === '—') { noSkuItems.push(item); return; }
    if (skuMap.has(item.sku)) {
      skuMap.get(item.sku).qty += item.qty;
    } else {
      skuMap.set(item.sku, { ...item });
    }
  });

  return { items: [...skuMap.values(), ...noSkuItems], warnings };
}
